import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { ApiKeyApiProvider, useApiKeyApi } from '../Services/ApiKeyApiProvider';
import { formatDateUTC } from '../utils/formatDate';
import { formatTableTitle } from '../utils/formatTitle';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';

function ApiKeysHub({ canEdit }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getApiKeys, deleteApiKey } = useApiKeyApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [totalKeys, setTotalKeys] = useState(0);

  const tableTitle = 'API Keys';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/api-key/${row.id}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(`Are you sure you want to delete this API key?`, async () => {
      try {
        setLoading(true);
        await deleteApiKey(selectedId);
        await fetchApiKeys(persistedSkip, persistedPageSize);
        showAlert('success', 'API Key deleted!');
      } catch (err) {
        console.error(err);
        showAlert('fail', 'Failed to delete API Key!');
      } finally {
        setLoading(false);
      }
    });
  };

  const fetchApiKeys = async (skip, limit) => {
    setLoading(true);
    try {
      const response = await getApiKeys(organizationId, skip, limit);
      console.log('Fetched API keys:', response);
      setApiKeys(response.items);
      setTotalKeys(response.totalCount);
      setError(null);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to load API keys');
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchApiKeys(persistedSkip, persistedPageSize);
  }, []);

  const columns = [
    { name: 'ID', selector: (row) => row.id },
    { name: 'Name', selector: (row) => row.name },
    {
      name: 'Expires At',
      selector: (row) => (row.expires_at ? formatDateUTC(row.expires_at) : '-'),
    },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive') },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at) },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at) },
  ];

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={apiKeys}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                paginationTotalRows={totalKeys}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchApiKeys}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/api-key/new-api-key`)}
                >
                  Create New API Key
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ApiKeysHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ApiKeyApiProvider>
        <ApiKeysHub canEdit={canEdit} />
      </ApiKeyApiProvider>
    </ConfirmationProvider>
  );
}

export default ApiKeysHubWrapper;
